import { useCallback } from 'react';

import {
  UseToggleForwardPhoneNumberParams,
  UseToggleForwardPhoneNumberReturnType,
} from './useToggleForwardPhoneNumber.decl';

import { UpdateAgentMutation, UpdateAgentMutationVariables } from '@generated/UpdateAgentMutation';
import { UPDATE_AGENT_MUTATION } from '@graphql/mutations/UpdateAgentMutation';
import { useGraphMutationWithFeedback } from '@hooks/useMutation';
import { useTranslation } from 'react-i18next';

/**
 * Hook to update a user forward rules related to a phone number given a phone number.
 * @param phoneNumber - object containing all information related to a given phone number
 * @returns an object holding a function responsible for updating a user forward rules related to a phone number and its state
 */
export function useToggleForwardPhoneNumber({
  user,
  phoneNumber: phoneNumberObject,
}: UseToggleForwardPhoneNumberParams): UseToggleForwardPhoneNumberReturnType {
  const { t } = useTranslation();
  const [mutate, { loading }] = useGraphMutationWithFeedback<
    UpdateAgentMutation,
    UpdateAgentMutationVariables
  >(UPDATE_AGENT_MUTATION, { type: 'snackbar' });

  const updatePhoneNumber = useCallback(
    async (isEnabled: boolean) => {
      const { ID, phoneNumber } = phoneNumberObject;

      await mutate(
        {
          variables: {
            input: {
              ID: user.ID,
              phoneNumbers: [
                {
                  ID,
                  phoneNumber,
                  isEnabled,
                },
              ],
            },
          },
          optimisticResponse: {
            updateAgent: {
              ...user,
              phoneNumbers: [{ ...phoneNumberObject, isEnabled }],
            },
          },
        },
        {
          successMessage: t('user_profile.call_preferences.forward_call_phone.toggle_feedback'),
        }
      );
    },
    [mutate, phoneNumberObject, t, user]
  );

  return { updatePhoneNumber, loading };
}
